import React, { useState } from "react";
import {
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Box,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentIcon from "@mui/icons-material/Assignment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Agents } from "../../types/master";
import { useNavigate } from "react-router-dom";
import styles from "./header.module.css";

type Persona = "Commercial User" | "Rewards Admin";

type HeaderProps = {
  userName?: string;
  isSidebarOpen?: () => void;
  onClickHelp?: () => void;
  onClickLogout?: () => void;
  currentAgent?: Agents;
  selectedPersona?: Persona;
  onPersonaChange?: (persona: Persona) => void;
};

const Header = ({
  onClickHelp,
  isSidebarOpen,
  currentAgent,
  userName = "User",
  selectedPersona = "Commercial User",
  onPersonaChange,
}: HeaderProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handlePersonaClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePersonaClose = () => {
    setAnchorEl(null);
  };

  const handlePersonaSelect = (persona: Persona) => {
    if (onPersonaChange) {
      onPersonaChange(persona);
    }
    handlePersonaClose();
  };

  const handleReadmeDownload = () => {
    try {
      if (!currentAgent?.agent_readme_path) {
        console.warn("No readme path available for this agent");
        return;
      }

      // Construct full blob URL with base URL and SAS token (same pattern as PromptModal)
      const downloadUrl = `${import.meta.env.VITE_TEMPLATE_BLOB_URL}/readme-templates/${currentAgent.agent_readme_path}?${import.meta.env.VITE_TEMPLATE_G42_FILES}`;
      const fileName =
        currentAgent.agent_readme_path.split("/").pop() || "Agent-ReadMe.pdf";

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.target = "_blank";
      link.download = decodeURIComponent(fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading readme file:", error);
    }
  };

  return (
    <div className={styles.header}>
      <div className={styles.leftSection}>
        <IconButton className={styles.menuButton} onClick={isSidebarOpen}>
          <MenuIcon />
        </IconButton>
        {/* <img src="/images/g42-logo.png" alt="G42 Logo" className={styles.logo} /> */}
      </div>
      <div className={styles.rightSection}>
        {currentAgent?.id === 26 && (
          <Tooltip title="View Procurement Dashboard" arrow>
            <IconButton
              className={styles.dashboardButton}
              onClick={() =>
                navigate("/procurement-dashboard/contracts-management")
              }
              aria-label="Go to Procurement Dashboard"
            >
              <AssignmentIcon
                fontSize="small"
                className={styles.dashboardIcon}
              />
              <span>Dashboard</span>
            </IconButton>
          </Tooltip>
        )}
        {currentAgent?.agent_readme_path && (
          <Tooltip title='Click to download "Read Me" file for the agent' arrow>
            <IconButton
              className={styles.readmeButton}
              onClick={handleReadmeDownload}
              aria-label="Download Read Me file"
            >
              <DescriptionIcon fontSize="small" className={styles.readmeIcon} />
              <span>Read Me</span>
            </IconButton>
          </Tooltip>
        )}
        {currentAgent?.is_man_power_admin === 1 && currentAgent?.id === 504 && (
          <>
            <Box
              className={styles.personaSwitcher}
              onClick={handlePersonaClick}
              sx={{ cursor: "pointer" }}
            >
              <Typography className={styles.personaText}>
                {selectedPersona} · {userName}
              </Typography>
              <KeyboardArrowDownIcon className={styles.personaIcon} />
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handlePersonaClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              className={styles.personaMenu}
            >
              <Box className={styles.menuSection}>
                <Typography className={styles.menuLabel}>
                  SWITCH PERSONA
                </Typography>
                <MenuItem
                  onClick={() => handlePersonaSelect("Commercial User")}
                  selected={selectedPersona === "Commercial User"}
                  className={styles.menuItem}
                >
                  Commercial User
                </MenuItem>
                <MenuItem
                  onClick={() => handlePersonaSelect("Rewards Admin")}
                  selected={selectedPersona === "Rewards Admin"}
                  className={styles.menuItem}
                >
                  Rewards Admin
                </MenuItem>
              </Box>
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};

export default Header;
